/**
 * Reading and writing the user's birth date, time and "is the time known"
 * flag (HORO-10581).
 *
 * Why this module exists: birth date and birth time used to be two halves of a
 * single stored `Date`, and that instant could not express two things the
 * chart depends on.
 *
 *  1. **Which clock the time was on.** `new Date(...).setHours(...)` builds an
 *     instant in the VIEWER's timezone, not the birthplace's. A user who moved
 *     from Mumbai to London saw a different birth time than the one they
 *     entered, and the server computed a rising sign from it.
 *  2. **Whether a time was given at all.** Noon was the app's sentinel for
 *     "unknown", which collides with a real noon birth. Someone born at 12:00
 *     PM could never record it: every screen read it back as "not set".
 *
 * So the wire format is now a plain birthplace-LOCAL wall clock string plus an
 * explicit boolean, matching what the API stores on `birthAddress`:
 *
 *     birthDateTime:  '1990-06-15T08:30:00'  (or '1990-06-15' with no time)
 *     birthTimeKnown: true | false
 *
 * No timezone conversion happens anywhere in here, deliberately. The string
 * means what it says, and the server resolves the birthplace zone itself.
 */

const pad = (n) => String(n).padStart(2, '0');

/** The legacy sentinel. Only consulted when `birthTimeKnown` is absent. */
const UNKNOWN_TIME_SENTINEL = '12:00:00';

/**
 * Read a user's birth details back, whatever shape they are stored in.
 *
 * Two tiers, most-trusted first:
 *
 *  1. `birthAddress.birthDateTime` + `birthTimeKnown` — an explicit local wall
 *     clock and an explicit answer to "did they tell us a time?".
 *  2. `birthDate` — a legacy instant. Read with LOCAL getters because that is
 *     how this app wrote it (`toIsoBirthDate` builds local midnight), so it
 *     round-trips to the date the user actually picked.
 *
 * @returns {{year:number, month:number, day:number, hour:number|null, minute:number|null, timeKnown:boolean}|null}
 */
export const readBirthInfo = (user) => {
    const stored = user?.birthAddress?.birthDateTime;
    const storedTimeKnown = user?.birthAddress?.birthTimeKnown;

    if (typeof stored === 'string' && stored.length >= 10) {
        const [datePart, timePart] = stored.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        if (!year || !month || !day) return null;

        const timeKnown =
            typeof storedTimeKnown === 'boolean'
                ? storedTimeKnown && !!timePart
                : !!timePart && !timePart.startsWith(UNKNOWN_TIME_SENTINEL);

        if (!timeKnown) return { year, month, day, hour: null, minute: null, timeKnown: false };

        const [hour, minute] = timePart.split(':').map(Number);
        if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
            return { year, month, day, hour: null, minute: null, timeKnown: false };
        }
        return { year, month, day, hour, minute: Number.isInteger(minute) ? minute : 0, timeKnown: true };
    }

    if (!user?.birthDate) return null;
    const instant = new Date(user.birthDate);
    if (Number.isNaN(instant.getTime())) return null;

    // Exactly midnight is the fingerprint of a date-only capture: the date
    // picker builds local midnight and attaches no time. Calling that a birth
    // time would fabricate a Moon and a rising sign.
    const dateOnly = instant.getHours() === 0 && instant.getMinutes() === 0;

    return {
        year: instant.getFullYear(),
        month: instant.getMonth() + 1,
        day: instant.getDate(),
        hour: dateOnly ? null : instant.getHours(),
        minute: dateOnly ? null : instant.getMinutes(),
        timeKnown: !dateOnly
    };
};

/**
 * Build the `birthDateTime` string to send.
 *
 * Omits the time entirely when it is unknown, rather than substituting noon —
 * the whole point of `birthTimeKnown` is that the value no longer has to carry
 * that meaning.
 */
export const toBirthDateTime = ({ year, month, day, hour, minute, timeKnown }) => {
    const date = `${year}-${pad(month)}-${pad(day)}`;
    if (!timeKnown || hour === null || hour === undefined) return date;
    return `${date}T${pad(hour)}:${pad(minute ?? 0)}:00`;
};

/** 24-hour clock → the wheel positions the time editor uses. */
export const to12Hour = (hour, minute) => ({
    hour: hour % 12 === 0 ? 12 : hour % 12,
    minute: minute ?? 0,
    meridiem: hour >= 12 ? 'PM' : 'AM'
});

/** Wheel positions → a 24-hour clock. 12 AM is 0 and 12 PM is 12 — the two
 *  cases a naive `hour + 12` gets wrong in opposite directions. */
export const to24Hour = (hour, meridiem) => {
    const base = hour % 12;
    return meridiem === 'PM' ? base + 12 : base;
};

/**
 * A POST /user/birth-chart body back into the birth chart flow's `details`
 * shape (HORO-10360): the anonymous "Generate" stashes the body across the
 * sign-up wall, and the post-auth resume needs the flow's form of it.
 *
 * @returns {{birthDate:string, birthTime:string|null, birthTimeKnown:boolean|null, place:{placeId:string, description:string}|null}}
 */
export const detailsFromGenerateBody = (body) => {
    const [birthDate = '', timePart] = String(body?.birthDateTime || '').split('T');
    const birthTime = body?.birthTimeKnown !== false && timePart ? timePart.slice(0, 5) : null;
    return {
        birthDate,
        birthTime,
        birthTimeKnown: typeof body?.birthTimeKnown === 'boolean' ? body.birthTimeKnown : null,
        place: body?.birthAddress?.placeId ? { placeId: body.birthAddress.placeId, description: body.birthAddress.description || '' } : null
    };
};

export default { readBirthInfo, toBirthDateTime, to12Hour, to24Hour, detailsFromGenerateBody };
